/**
 * CANONICAL PUBLIC NAVIGATION CONFIGURATION
 * Elvis Aloha Collection
 * 
 * This is the single source of truth for all public navigation.
 * All headers, footers, mobile menus must derive from this configuration.
 * 
 * DO NOT create separate navigation arrays in individual pages.
 * 
 * APPROVED STRUCTURE:
 * - Collection → /collection
 * - Arena → /ticket-registry/map
 * - Tickets → /ticket-registry/list
 * - Research → /research
 * - About → /about
 */

// Primary navigation items - used in header, mobile menu
export const PUBLIC_NAV_ITEMS = [
  { 
    label: 'Collection', 
    href: '/collection',
    matches: ['/collection', '/artifacts', '/catalog'],
    includeChildren: true
  },
  { 
    label: 'Arena', 
    href: '/ticket-registry/map',
    matches: ['/arena', '/ticket-registry/map', '/seat-map'],
    includeChildren: false
  },
  { 
    label: 'Tickets', 
    href: '/ticket-registry/list',
    matches: ['/tickets', '/ticket-registry/list', '/ticket-registry', '/registry'],
    includeChildren: true
  },
  { 
    label: 'Research', 
    href: '/research',
    matches: ['/research', '/studies', '/facts', '/methodology', '/governance/methodology'],
    includeChildren: true
  },
  { 
    label: 'About', 
    href: '/about',
    matches: ['/about', '/about-archive', '/governance', '/accessibility', '/privacy', '/rights', '/corrections'],
    includeChildren: true
  }
];

// Mobile bottom navigation (5 items max for thumb reach)
export const MOBILE_NAV_ITEMS = [
  { label: 'Home', href: '/', icon: 'home' },
  { label: 'Collection', href: '/collection', icon: 'archive' },
  { label: 'Arena', href: '/ticket-registry/map', icon: 'map' },
  { label: 'Tickets', href: '/ticket-registry/list', icon: 'ticket' },
  { label: 'Research', href: '/research', icon: 'book' }
];

// Footer primary links
export const FOOTER_PRIMARY_LINKS = [
  { label: 'Collection', href: '/collection' },
  { label: 'Arena', href: '/ticket-registry/map' },
  { label: 'Tickets', href: '/ticket-registry/list' },
  { label: 'Research', href: '/research' },
  { label: 'About', href: '/about' }
];

// Footer secondary links
export const FOOTER_SECONDARY_LINKS = [
  { label: 'Governance', href: '/governance' },
  { label: 'Methodology', href: '/governance/methodology' },
  { label: 'Accessibility', href: '/accessibility' },
  { label: 'Privacy', href: '/privacy' },
  { label: 'Rights & Reproductions', href: '/rights' }
];

// Legacy route redirects - old routes that should redirect to new structure
export const LEGACY_REDIRECTS = {
  '/event': '/',
  '/collection-story': '/',
  '/artifacts': '/collection',
  '/timeline': '/research',
  '/event/timeline': '/research',
  '/registry': '/ticket-registry/list',
  '/seat-map': '/ticket-registry/map',
  '/catalog': '/collection',
  '/studies': '/research',
  '/arena': '/ticket-registry/map',
  '/tickets': '/ticket-registry/list'
};

/**
 * Check if a navigation item is active based on current path
 * @param {Object} item - Navigation item with matches array
 * @param {string} currentPath - Current location pathname
 * @returns {boolean}
 */
export const isNavItemActive = (item, currentPath) => {
  if (!currentPath || currentPath === '/') return false;
  
  const normalizedPath = currentPath.replace(/\/+$/, '');
  
  return item.matches.some((route) => {
    const normalizedRoute = route.replace(/\/+$/, '');
    if (normalizedPath === normalizedRoute) return true;
    return item.includeChildren && normalizedPath.startsWith(`${normalizedRoute}/`);
  });
};
